



import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
    InstagramProfile, 
    ContentPiece, 
    VideoProject,
    Goal,
    Language,
    AutomationRule,
    Lead,
    Funnel,
    Product,
    Category,
    Order,
    Coupon,
    Restaurant,
    OrderStatus
} from '../types';
import { supabase } from '../services/supabaseClient';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';
// FIX: Import mock data to initialize new state properties.
import { MOCK_RESTAURANT, MOCK_CATEGORIES, MOCK_PRODUCTS, MOCK_ORDERS, MOCK_COUPONS } from '../services/mockData';

interface StoreContextType {
  business: InstagramProfile | null;
  isLoading: boolean;
  language: Language;
  currency: string;
  
  // Core Actions
  createBusiness: (data: any) => Promise<void>;
  updateBusiness: (data: Partial<InstagramProfile>) => Promise<void>;
  deleteBusiness: () => Promise<void>;
  
  // Video Engine
  videoProjects: VideoProject[];
  saveVideoProject: (project: Partial<VideoProject>) => Promise<void>;
  deleteVideoProject: (id: string) => Promise<void>;
  
  // Content Strategy
  contentCalendar: ContentPiece[];
  addContentPiece: (piece: Partial<ContentPiece>) => Promise<void>;
  deleteContentPiece: (id: string) => Promise<void>;
  
  // Goals
  goals: Goal[];
  addGoal: (goal: Partial<Goal>) => void;
  updateGoalProgress: (id: string, value: number) => void;

  // Session Cache
  sessionCache: Record<string, any>;
  updateSessionCache: (key: string, data: any) => void;

  // Automations
  automations: AutomationRule[];
  addAutomation: (rule: Partial<AutomationRule>) => void;
  toggleAutomation: (id: string, active: boolean) => void;

  // Leads
  leads: Lead[];
  addLead: (lead: Partial<Lead>) => Promise<void>;
  deleteLead: (id: string) => Promise<void>;

  // Funnels
  funnels: Funnel[];
  addFunnel: (funnel: Partial<Funnel>) => void;
  deleteFunnel: (id: string) => void;

  // FIX: Add e-commerce related properties to the context type to fix widespread errors.
  restaurant: Restaurant | null;
  products: Product[];
  categories: Category[];
  orders: Order[];
  coupons: Coupon[];
  fetchRestaurantBySlug: (slug: string) => Promise<void>;
  addCategory: (name: string) => Promise<void>;
  deleteCategory: (id: string) => Promise<void>;
  updateCategory: (category: Category) => Promise<void>;
  addItem: (item: Partial<Product>) => Promise<Product | null>;
  updateProduct: (product: Product) => Promise<void>;
  deleteProduct: (id: string) => Promise<void>;
  createCoupon: (code: string, discount: number) => Promise<void>;
  deleteCoupon: (id: string) => Promise<void>;
  updateSaleStatus: (id: string, status: OrderStatus) => Promise<void>;
}

const StoreContext = createContext<StoreContextType | undefined>(undefined);

export const StoreProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const { addToast } = useToast();
  
  // 1. REGION & LANGUAGE DETECTION
  const [language, setLanguage] = useState<Language>('en');
  const [currency, setCurrency] = useState('USD');

  useEffect(() => {
      const browserLang = navigator.language || (navigator as any).userLanguage;
      if (browserLang.toLowerCase().includes('pt')) {
          setLanguage('pt-BR');
          setCurrency('BRL');
      } else {
          setLanguage('en');
          setCurrency('USD');
      }
  }, []);

  // 2. STATE WITH LOCAL STORAGE PERSISTENCE INITIALIZATION
  const getPersisted = (key: string, def: any) => {
      if (typeof window === 'undefined') return def;
      try {
          const item = localStorage.getItem(`herofrog_${key}`);
          return item ? JSON.parse(item) : def;
      } catch { return def; }
  };

  const [business, setBusiness] = useState<InstagramProfile | null>(() => getPersisted('business', null));
  const [videoProjects, setVideoProjects] = useState<VideoProject[]>(() => getPersisted('videos', []));
  const [contentCalendar, setContentCalendar] = useState<ContentPiece[]>(() => getPersisted('content', []));
  const [goals, setGoals] = useState<Goal[]>(() => getPersisted('goals', []));
  
  const [sessionCache, setSessionCache] = useState<Record<string, any>>({});
  const [automations, setAutomations] = useState<AutomationRule[]>(() => getPersisted('automations', []));
  const [leads, setLeads] = useState<Lead[]>(() => getPersisted('leads', []));
  const [funnels, setFunnels] = useState<Funnel[]>(() => getPersisted('funnels', []));
  
  // FIX: Add state for e-commerce entities.
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [categories, setCategories] = useState<Category[]>(MOCK_CATEGORIES);
  const [products, setProducts] = useState<Product[]>(MOCK_PRODUCTS);
  const [orders, setOrders] = useState<Order[]>(MOCK_ORDERS);
  const [coupons, setCoupons] = useState<Coupon[]>(MOCK_COUPONS);

  const [isLoading, setIsLoading] = useState(true);

  // 3. PERSISTENCE EFFECT (SAVE ON CHANGE)
  useEffect(() => { localStorage.setItem('herofrog_business', JSON.stringify(business)); }, [business]);
  useEffect(() => { localStorage.setItem('herofrog_videos', JSON.stringify(videoProjects)); }, [videoProjects]);
  useEffect(() => { localStorage.setItem('herofrog_content', JSON.stringify(contentCalendar)); }, [contentCalendar]);
  useEffect(() => { localStorage.setItem('herofrog_goals', JSON.stringify(goals)); }, [goals]);
  useEffect(() => { localStorage.setItem('herofrog_automations', JSON.stringify(automations)); }, [automations]);
  useEffect(() => { localStorage.setItem('herofrog_leads', JSON.stringify(leads)); }, [leads]);
  useEffect(() => { localStorage.setItem('herofrog_funnels', JSON.stringify(funnels)); }, [funnels]);

  // 4. DATABASE SYNC (SUPABASE)
  useEffect(() => {
      if (authLoading) return;
      if (!user) {
          setIsLoading(false);
          return;
      }

      const syncData = async () => {
          setIsLoading(true);
          try {
              // Fetch Profile
              const { data: bizData } = await supabase.from('restaurants').select('*').eq('user_id', user.id).maybeSingle();
              if (bizData) {
                  // FIX: Changed type from InstagramProfile to Restaurant to include 'styles' and other restaurant-specific properties.
                  const mappedBusiness: Restaurant = {
                      id: bizData.id,
                      username: bizData.name,
                      slug: bizData.slug,
                      niche: bizData.niche,
                      plan: bizData.plan as any,
                      followersCount: bizData.followers_count,
                      postsCount: bizData.posts_count,
                      engagementRate: bizData.engagement_rate,
                      logoUrl: bizData.logo_url,
                      setupCompleted: true,
                      targetAudience: bizData.target_audience,
                      brandVoice: bizData.brand_voice,
                      // orderflow properties
                      styles: bizData.styles,
                      currency: bizData.currency,
                      isOpen: bizData.is_open,
                      minOrderValue: bizData.min_order_value,
                      address: bizData.address,
                      phone: bizData.phone,
                      delivery: bizData.delivery,
                      payment: bizData.payment,
                      landing: bizData.landing,
                      social: bizData.social,
                      aiConfig: bizData.ai_config,
                  };
                  setBusiness(mappedBusiness);
                  setRestaurant(mappedBusiness); // Also set the public restaurant object

                  // Fetch Videos
                  const { data: vids } = await supabase.from('video_projects').select('*').eq('restaurant_id', bizData.id);
                  if (vids) {
                      setVideoProjects(vids.map(v => ({
                          id: v.id,
                          name: v.name,
                          status: v.status,
                          thumbnail: v.thumbnail_url,
                          duration: v.duration,
                          aspectRatio: v.aspect_ratio,
                          lastModified: v.updated_at,
                          timelineData: v.timeline_data
                      })));
                  }

                  // Fetch Content
                  const { data: cont } = await supabase.from('content_items').select('*').eq('restaurant_id', bizData.id);
                  if (cont) {
                      setContentCalendar(cont.map(c => ({
                          id: c.id,
                          title: c.title,
                          format: c.format as any,
                          status: c.status as any,
                          aiScript: c.ai_script,
                          metrics: c.performance_metrics
                      })));
                  }
              }
          } catch (e) {
              console.error("Sync error", e);
          } finally {
              setIsLoading(false);
          }
      };
      syncData();
  }, [user, authLoading]);

  // --- ACTIONS ---

  const createBusiness = async (data: any) => {
      if (!user) return;
      const { data: inserted, error } = await supabase.from('restaurants').insert({
          user_id: user.id,
          name: data.username,
          slug: data.username.replace(/[^a-z0-9]/gi, '').toLowerCase(),
          niche: data.niche,
          logo_url: data.logoUrl,
          plan: 'PRO'
      }).select().single();

      if (error) throw error;
      setBusiness(inserted as any);
  };

  const updateBusiness = async (data: Partial<InstagramProfile>) => {
      if (!business) return;
      const { error } = await supabase.from('restaurants').update({
          niche: data.niche,
          target_audience: data.targetAudience,
          brand_voice: data.brandVoice
      }).eq('id', business.id);
      
      if (!error) setBusiness(prev => prev ? { ...prev, ...data } : null);
  };

  const deleteBusiness = async () => {
    if (!business) return;
    const { error } = await supabase.from('restaurants').delete().eq('id', business.id);
    if (!error) {
        setBusiness(null);
        // Clear local storage items for business
        localStorage.removeItem('herofrog_business');
        localStorage.removeItem('herofrog_videos');
        localStorage.removeItem('herofrog_content');
        localStorage.removeItem('herofrog_goals');
        localStorage.removeItem('herofrog_automations');
        localStorage.removeItem('herofrog_leads');
        localStorage.removeItem('herofrog_funnels');
        
        // Reset states
        setVideoProjects([]);
        setContentCalendar([]);
        setGoals([]);
        setAutomations([]);
        setLeads([]);
        setFunnels([]);
    } else {
        throw error;
    }
  };

  const saveVideoProject = async (project: Partial<VideoProject>) => {
      if (!business) return;
      
      const projectData = {
          name: project.name || 'Untitled Project',
          status: project.status || 'DRAFT',
          thumbnail_url: project.thumbnail,
          duration: project.duration || 0,
          aspect_ratio: project.aspectRatio || '9:16',
          timeline_data: project.timelineData || {},
          updated_at: new Date().toISOString()
      };

      if (project.id && videoProjects.find(p => p.id === project.id)) {
          // Update
          const { error } = await supabase.from('video_projects').update(projectData).eq('id', project.id);
          if (!error) {
              setVideoProjects(prev => prev.map(p => p.id === project.id ? { ...p, ...project, lastModified: new Date().toISOString() } : p));
              addToast(language === 'pt-BR' ? 'Projeto Salvo' : 'Project Saved', 'success');
          }
      } else {
          // Insert
          const { data, error } = await supabase.from('video_projects').insert({
              restaurant_id: business.id,
              ...projectData
          }).select().single();
          
          if (!error && data) {
              setVideoProjects(prev => [{
                  id: data.id,
                  name: data.name,
                  status: data.status,
                  thumbnail: data.thumbnail_url,
                  duration: data.duration,
                  aspectRatio: data.aspect_ratio,
                  lastModified: data.updated_at,
                  timelineData: data.timeline_data
              }, ...prev]);
              addToast(language === 'pt-BR' ? 'Projeto Criado' : 'Project Created', 'success');
          }
      }
  };

  const deleteVideoProject = async (id: string) => {
      const { error } = await supabase.from('video_projects').delete().eq('id', id);
      if (!error) {
          setVideoProjects(prev => prev.filter(p => p.id !== id));
      }
  };

  const addContentPiece = async (piece: Partial<ContentPiece>) => {
      if (!business) return;
      const { data, error } = await supabase.from('content_items').insert({
          restaurant_id: business.id,
          title: piece.title,
          format: piece.format,
          status: piece.status,
          ai_script: piece.aiScript
      }).select().single();

      if (!error && data) {
          setContentCalendar(prev => [{
              id: data.id,
              title: data.title,
              format: data.format as any,
              status: data.status as any,
              aiScript: data.ai_script,
              metrics: data.performance_metrics
          }, ...prev]);
      }
  };

  const deleteContentPiece = async (id: string) => {
      const { error } = await supabase.from('content_items').delete().eq('id', id);
      if (!error) setContentCalendar(prev => prev.filter(c => c.id !== id));
  };

  const addGoal = (goal: Partial<Goal>) => {
      setGoals(prev => [...prev, { id: Math.random().toString(), ...goal } as Goal]);
  };

  const updateGoalProgress = (id: string, value: number) => {
      setGoals(prev => prev.map(g => g.id === id ? { ...g, currentValue: value } : g));
  };

  const updateSessionCache = (key: string, data: any) => {
      setSessionCache(prev => ({ ...prev, [key]: data }));
  };

  const addAutomation = (rule: Partial<AutomationRule>) => {
      const newRule: AutomationRule = {
          id: Math.random().toString(36).substr(2, 9),
          name: rule.name || 'New Rule',
          active: true,
          triggerType: rule.triggerType || 'COMMENT',
          triggerKeywords: rule.triggerKeywords || [],
          responseContent: rule.responseContent || [],
          stats: { triggered: 0, converted: 0 },
          ...rule
      };
      setAutomations(prev => [...prev, newRule]);
  };

  const toggleAutomation = (id: string, active: boolean) => {
      setAutomations(prev => prev.map(a => a.id === id ? { ...a, active: !active } : a));
  };

  const addLead = async (lead: Partial<Lead>) => {
      const newLead: Lead = {
          id: Math.random().toString(36).substr(2, 9),
          instagramHandle: lead.instagramHandle || '@unknown',
          status: lead.status || 'COLD',
          tags: lead.tags || [],
          lastInteraction: new Date().toISOString(),
          ...lead
      };
      setLeads(prev => [...prev, newLead]);
  };

  const deleteLead = async (id: string) => {
      setLeads(prev => prev.filter(l => l.id !== id));
  };

  const addFunnel = (funnel: Partial<Funnel>) => {
      const newFunnel: Funnel = {
          id: Math.random().toString(36).substr(2, 9),
          name: funnel.name || 'Untitled Funnel',
          steps: funnel.steps || [],
          conversionRate: 0,
          totalLeads: 0,
          ...funnel
      };
      setFunnels(prev => [...prev, newFunnel]);
  };

  const deleteFunnel = (id: string) => {
      setFunnels(prev => prev.filter(f => f.id !== id));
  };

  // FIX: Implement missing e-commerce functions
  const fetchRestaurantBySlug = async (slug: string) => {
    setIsLoading(true);
    // In real app, fetch from supabase, using mock for demo
    if (slug === MOCK_RESTAURANT.slug) {
        setRestaurant(MOCK_RESTAURANT);
    } else {
        const { data } = await supabase.from('restaurants').select('*').eq('slug', slug).single();
        if(data) {
             // FIX: Changed type from InstagramProfile to Restaurant to include 'styles' and other restaurant-specific properties.
             const mappedBusiness: Restaurant = {
                id: data.id,
                username: data.name,
                slug: data.slug,
                niche: data.niche,
                plan: data.plan as any,
                followersCount: data.followers_count,
                postsCount: data.posts_count,
                engagementRate: data.engagement_rate,
                logoUrl: data.logo_url,
                setupCompleted: true,
                targetAudience: data.target_audience,
                brandVoice: data.brand_voice,
                styles: data.styles,
                currency: data.currency,
                isOpen: data.is_open,
                minOrderValue: data.min_order_value,
                address: data.address,
                phone: data.phone,
                delivery: data.delivery,
                payment: data.payment,
                landing: data.landing,
                social: data.social,
                aiConfig: data.ai_config,
            };
            setRestaurant(mappedBusiness);
        }
    }
    setIsLoading(false);
  };
  const addCategory = async (name: string) => { setCategories(p => [...p, {id: ''+Date.now(), name, active: true}]) };
  const deleteCategory = async (id: string) => setCategories(p => p.filter(c => c.id !== id));
  const updateCategory = async (cat: Category) => setCategories(p => p.map(c => c.id === cat.id ? cat : c));
  const addItem = async (item: Partial<Product>): Promise<Product|null> => {
    const newItem = { id: ''+Date.now(), ...item, active: true, inStock: true, groups: [], imageUrl: 'https://picsum.photos/400/300' } as Product;
    setProducts(p => [...p, newItem]);
    return newItem;
  };
  const updateProduct = async (prod: Product) => setProducts(p => p.map(c => c.id === prod.id ? prod : c));
  const deleteProduct = async (id: string) => setProducts(p => p.filter(c => c.id !== id));
  const createCoupon = async (code: string, discount: number) => setCoupons(p => [...p, {id: ''+Date.now(), code, discountPercentage: discount}]);
  const deleteCoupon = async (id: string) => setCoupons(p => p.filter(c => c.id !== id));
  const updateSaleStatus = async (id: string, status: OrderStatus) => setOrders(p => p.map(o => o.id === id ? {...o, status} : o));


  return (
    <StoreContext.Provider value={{
        business, isLoading, language, currency,
        createBusiness, updateBusiness, deleteBusiness,
        videoProjects, saveVideoProject, deleteVideoProject,
        contentCalendar, addContentPiece, deleteContentPiece,
        goals, addGoal, updateGoalProgress,
        sessionCache, updateSessionCache,
        automations, addAutomation, toggleAutomation,
        leads, addLead, deleteLead,
        funnels, addFunnel, deleteFunnel,
        // FIX: Provide new properties through context
        restaurant,
        products,
        categories,
        orders,
        coupons,
        fetchRestaurantBySlug,
        addCategory,
        deleteCategory,
        updateCategory,
        addItem,
        updateProduct,
        deleteProduct,
        createCoupon,
        deleteCoupon,
        updateSaleStatus
    }}>
        {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => {
  const context = useContext(StoreContext);
  if (!context) throw new Error('useStore must be used within a StoreProvider');
  return context;
};
